import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoalType } from '../../components/molecules/GoalType/GoalType';
import { useAppSelector, useAppDispatch, useIsMultiPortfolioTenant, usePlanOverview } from '../../common/hooks';
import { TabType } from '../../common/types/plan-overview-entity';
import { InfoTooltip, PrimaryTabs, SecondaryTabs } from '../../components/molecules';
import {
    GoalOverviewContent,
    PortfolioDetailAndProjectionsContent
    // GoalEditComparison
} from '../../components/organisms';
import { ContainerBox, ContentContainer } from '../../themes/styles/common';
import { getGoalPriorityProbability, getHeaderIconFromType } from '../../common/helper';
import { useTranslation } from 'react-i18next';
import { SingleSelectMenu } from '../../components/molecules/SingleSelectMenu';
import {
    checkFFEnabled,
    getClientJourneyFlowType,
    getEligibleGoals,
    retrySingleMpGoalCalculator,
    sortGoalsByPriorityOrder
} from '../../utils';
import { DEFAULT_GOAL_CATEGORY, EWS_DURATIONS, EWS_DURATION_KEYS, EwsDuration, FEATURE_NAMES } from '../../common/constants';
import { TabsWrapper } from '../../themes/styles/plan-overview';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { Icon, Typography } from '../../components/atoms';
import { goalCategoryPortfolioMappingsType } from '../../common/types';
import { ADVISOR_ROUTES } from '../../routes/routeConstants';
import { FailedToLoadContent } from '../../components/organisms/FailedToLoadContent';
import { selectEwsResponses } from '../../features/ews';

const PlanOverview = () => {
    const {
        selectedGoalIndex,
        selectedSecondaryTab,
        handleGoalChange,
        handleSecondaryTabChange,
        handleEditPlan
        // handleExitEditPlan,
        // isGoalInEditMode
    } = usePlanOverview();
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    // const goalEditing = isGoalInEditMode(selectedGoalIndex);
    const adviceGoals = useAppSelector((state) => state?.adviceGoals);
    const adviceClient = useAppSelector((state) => state?.adviceClient);
    const { goeConfig } = useAppSelector((state) => state.global);
    const {
        formFields,
        response,
        global: { responseGoalCalculator }
    } = adviceGoals;
    const global = useAppSelector((state) => state?.global);
    const tenant = useAppSelector((state) => state.auth?.user?.tenant || state.global?.globalConfig?.subdomain);
    const orgEmail = global?.orgInfo?.orgEmail || '';
    const goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined = useAppSelector(
        (state) => state?.global?.globalConfig?.goalCategoryPortfolioMappings
    );
    const isPlanDetailsEnhancementEnabled = checkFFEnabled(FEATURE_NAMES['PLAN_DETAILS_ENHANCEMENT_FE_AEX-2554']);
    const isFlow2 = getClientJourneyFlowType(tenant) === 'flow2';
    const ewsResponsesState = useAppSelector(selectEwsResponses);
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();

    // In flow2, Plan Overview must only display goals whose tenure has eligible funding sources.
    // We keep `selectedGoalIndex` relative to the filtered (eligible) tab list and translate
    // back to the original formFields index when reading parallel arrays / passing to children.
    // In other flows we use `formFields` directly (no eligibility filtering).
    const eligibleFormFields = useMemo(
        () =>
            isFlow2
                ? getEligibleGoals(
                      adviceClient,
                      { formFields },
                      global?.orgInfo?.incomeSourceType?.en,
                      global?.orgInfo?.investmentAccountType?.en
                  )
                : formFields,
        [
            isFlow2,
            adviceClient,
            formFields,
            global?.orgInfo?.incomeSourceType?.en,
            global?.orgInfo?.investmentAccountType?.en
        ]
    );
    const orderedEligibleFormFields = useMemo(() => {
        if (!(isFlow2 && isPlanDetailsEnhancementEnabled)) return eligibleFormFields;
        const ewsResponses = ewsResponsesState;
        if (!isMultiPortfolioTenant) {
            return sortGoalsByPriorityOrder(
                eligibleFormFields,
                ewsResponses?.[DEFAULT_GOAL_CATEGORY]?.response?.body?.runPipeResponses
            );
        }
        const byTerm = new Map<string, any[]>();
        eligibleFormFields.forEach((goal: any) => {
            const term = goal?.data?.goalCategory ?? '';
            if (!byTerm.has(term)) byTerm.set(term, []);
            byTerm.get(term)!.push(goal);
        });
        return Array.from(byTerm.entries()).flatMap(([term, goalsInTerm]) => {
            const runPipeResponses =
                ewsResponses?.[EWS_DURATION_KEYS[term as EwsDuration]]?.response?.body?.runPipeResponses;
            return sortGoalsByPriorityOrder(goalsInTerm, runPipeResponses);
        });
    }, [isFlow2, isPlanDetailsEnhancementEnabled, isMultiPortfolioTenant, eligibleFormFields, ewsResponsesState]);
    const eligibleIndexToOriginal = useMemo(
        () =>
            orderedEligibleFormFields.map((eligibleField: any) =>
                formFields.findIndex((ff: any) => ff.goalId === eligibleField.goalId)
            ),
        [orderedEligibleFormFields, formFields]
    );
    const originalSelectedGoalIndex = useMemo(
        () => eligibleIndexToOriginal[selectedGoalIndex] ?? 0,
        [eligibleIndexToOriginal, selectedGoalIndex]
    );

    const selectedGoalId = useMemo(
        () => formFields[originalSelectedGoalIndex]?.goalId,
        [formFields, originalSelectedGoalIndex]
    );

    const selectedGoalCalculatorResult = useMemo(
        () => responseGoalCalculator?.[originalSelectedGoalIndex],
        [responseGoalCalculator, originalSelectedGoalIndex]
    );
    // Plan-detail sections need the runpipe `response` for the selected goal; a persisted plan may have
    // stale goal-calculator entries without it. Gate readiness on it to show "Failed to Load Data" not crash.
    const hasSelectedGoalRunpipeResponse = useMemo(
        () => !!response?.find((item: any) => item?.goalId === selectedGoalId)?.response?.body,
        [response, selectedGoalId]
    );
    const isMpGoalCalcLoading = useMemo(() => !!selectedGoalCalculatorResult?.loading, [selectedGoalCalculatorResult]);
    const isMpGoalCalcError = useMemo(
        () =>
            !!selectedGoalCalculatorResult?.error ||
            (!selectedGoalCalculatorResult?.loading &&
                (!selectedGoalCalculatorResult?.response || !hasSelectedGoalRunpipeResponse)),
        [selectedGoalCalculatorResult, hasSelectedGoalRunpipeResponse]
    );
    const isMpGoalCalcReady = useMemo(
        () =>
            !isMpGoalCalcLoading &&
            !isMpGoalCalcError &&
            !!selectedGoalCalculatorResult?.response &&
            hasSelectedGoalRunpipeResponse,
        [isMpGoalCalcLoading, isMpGoalCalcError, selectedGoalCalculatorResult, hasSelectedGoalRunpipeResponse]
    );

    const handleMpGoalCalcRetry = useCallback(() => {
        if (!selectedGoalId) return;
        retrySingleMpGoalCalculator(dispatch, adviceGoals, orgEmail, selectedGoalId, goalCategoryPortfolioMappings);
    }, [dispatch, adviceGoals, orgEmail, selectedGoalId, goalCategoryPortfolioMappings]);
    const goalPriorityValue = goeConfig?.['goalPriorityProbabilities'];
    const tabItemContents = orderedEligibleFormFields.map((field: any, index: number) => {
        const thresholdValue =
            Math.round(getGoalPriorityProbability(goalPriorityValue, field?.data?.['goalPriority']) * 100) || 0;
        const originalIndex = eligibleIndexToOriginal[index];

        return {
            ...field,
            label: (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <GoalType
                            type={getHeaderIconFromType(field?.data?.wealthGoalType) || 'buyahouse'}
                            size={isPlanDetailsEnhancementEnabled ? 10 : 'small'}
                            width={isPlanDetailsEnhancementEnabled ? 16 : undefined}
                            height={isPlanDetailsEnhancementEnabled ? 16 : undefined}
                        />
                        {field?.data?.goalName}
                    </div>
                    {Math.round(
                        Number(response[originalIndex]?.response?.body?.analysisReport?.currentGoalProbability) * 100
                    ) < thresholdValue && (
                        <span style={{ marginLeft: '6px', display: 'inline-flex' }} aria-label="Warning">
                            <Icon id="warning-triangle" size={18} />
                        </span>
                    )}
                </>
            )
        };
    });

    const goalSets = formFields?.reduce((acc, field) => {
        const category = field?.data?.goalCategory;
        if (category && !acc.includes(category)) {
            acc.push(category);
        }
        return acc;
    }, [] as string[]);

    // Empty-term placeholders only exist for term based (multiportfolio) tenants.
    if (isFlow2 && isPlanDetailsEnhancementEnabled && isMultiPortfolioTenant) {
        EWS_DURATIONS.forEach((category) => {
            if (goalSets?.length === 0 || !goalSets?.includes(category)) {
                tabItemContents.push({
                    label: (
                        <InfoTooltip
                            placement={'bottom-end'}
                            width={260}
                            t={'TERM_EMPTY_TOOLTIP'}
                            messageVariant="labels.small"
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                boxSizing: 'border-box',
                                gap: '1rem'
                            }}
                            showInfoIcon
                            infoIconSize={16}
                            showInfoOnClick
                        >
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ width: '16px' }}></div>
                                {t('TEXT_NONE')}
                            </div>
                        </InfoTooltip>
                    ),
                    disabled: true,
                    data: {
                        goalCategoryOrder: category === 'Short Term' ? 1 : category === 'Medium Term' ? 2 : 3,
                        goalCategory: category
                    }
                } as typeof formFields[0]);
            }
        });
    }

    const newTabItemContents = [
        { label: t('LABEL_GOAL_OVERVIEW') },
        {
            label: t('LABEL_PORTFOLIO_DETAIL_PROJECTIONS'),
            disabled:
                response?.[originalSelectedGoalIndex]?.response?.body?.analysisReport?.currentGoalProbability * 100 < 1,
            ...(response?.[originalSelectedGoalIndex]?.response?.body?.analysisReport?.currentGoalProbability * 100 < 1
                ? { tooltipKey: 'TOOLTIP_PORTFOLIO_DETAILS_DISABLED' }
                : {})
        }
    ];

    useEffect(() => {
        if (location.pathname !== ADVISOR_ROUTES.PLAN_DETAILS) navigate(ADVISOR_ROUTES.PLAN_DETAILS, { replace: true });
    }, []);

    return (
        <ContainerBox>
            {isPlanDetailsEnhancementEnabled && (
                <div style={{ display: 'flex', width: '100%', gap: '16px', minWidth: 0 }}>
                    <SingleSelectMenu
                        value={selectedGoalIndex}
                        onChange={(event, index) => {
                            if (tabItemContents[index]?.disabled) return;
                            handleGoalChange(event, index);
                        }}
                        tabItems={tabItemContents}
                        flat={!isMultiPortfolioTenant}
                    />
                    <TabsWrapper>
                        {/* mt keeps the selected tab's top border from being clipped */}
                        <Box sx={{ marginTop: theme.spacing(1) }}>
                            <PrimaryTabs
                                ftdsBadge
                                value={selectedSecondaryTab}
                                onChange={handleSecondaryTabChange}
                                tabItems={newTabItemContents}
                            />
                        </Box>

                        {/* FTDS: content is a separate bordered card below the tab bar (gap + full
                            border + 8px radius), not attached to the tabs. */}
                        <ContentContainer
                            sx={{
                                marginTop: '24px',
                                border: `1px solid ${theme.palette.semantic.bd.default}`,
                                borderRadius: '8px'
                            }}
                        >
                            {isMpGoalCalcLoading ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: '60vh',
                                        gap: 2
                                    }}
                                >
                                    <CircularProgress
                                        size={80}
                                        thickness={4}
                                        sx={{ color: theme.palette.primary.main }}
                                    />
                                    <Typography
                                        variant="body.large"
                                        label={t('PLEASE_WAIT_WHILE_DATA_LOADS')}
                                        color="neutral.grey.400"
                                    />
                                </Box>
                            ) : isMpGoalCalcError ? (
                                <FailedToLoadContent onRetry={() => handleMpGoalCalcRetry()} />
                            ) : isMpGoalCalcReady ? (
                                <>
                                    {selectedSecondaryTab === TabType.GoalOverview && (
                                        <GoalOverviewContent selectedGoalIndex={originalSelectedGoalIndex} />
                                    )}
                                    {selectedSecondaryTab === TabType.PortfolioDetail && (
                                        <PortfolioDetailAndProjectionsContent
                                            selectedGoalIndex={originalSelectedGoalIndex}
                                        />
                                    )}
                                </>
                            ) : null}
                        </ContentContainer>
                    </TabsWrapper>
                </div>
            )}
            {!isPlanDetailsEnhancementEnabled && (
                <>
                    <PrimaryTabs value={selectedGoalIndex} onChange={handleGoalChange} tabItems={tabItemContents} />

                    <ContentContainer>
                        <SecondaryTabs
                            value={selectedSecondaryTab}
                            onChange={handleSecondaryTabChange}
                            onEditPlan={handleEditPlan}
                            selectedGoalIndex={originalSelectedGoalIndex}
                            showSecondTab={
                                response?.[originalSelectedGoalIndex]?.response?.body?.analysisReport
                                    ?.isGoalRealistic ?? false
                            }
                            // isEditing={goalEditing}
                            // onExit={() => handleExitEditPlan(selectedGoalIndex)}
                        />

                        {isMpGoalCalcLoading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: '60vh',
                                    gap: 2
                                }}
                            >
                                <CircularProgress size={80} thickness={4} sx={{ color: 'secondary.purple.400' }} />
                                <Typography
                                    variant="body.large"
                                    label={t('PLEASE_WAIT_WHILE_DATA_LOADS')}
                                    color="neutral.grey.400"
                                />
                            </Box>
                        ) : isMpGoalCalcError ? (
                            <FailedToLoadContent onRetry={() => handleMpGoalCalcRetry()} />
                        ) : isMpGoalCalcReady ? (
                            <>
                                {selectedSecondaryTab === TabType.GoalOverview && (
                                    <GoalOverviewContent selectedGoalIndex={originalSelectedGoalIndex} />
                                )}

                                {/* {selectedSecondaryTab === TabType.GoalOverview && goalEditing && (
                                        <GoalEditComparison selectedGoalIndex={selectedGoalIndex} />
                                    )} */}

                                {selectedSecondaryTab === TabType.PortfolioDetail && (
                                    <PortfolioDetailAndProjectionsContent
                                        selectedGoalIndex={originalSelectedGoalIndex}
                                    />
                                )}
                            </>
                        ) : null}
                    </ContentContainer>
                </>
            )}
        </ContainerBox>
    );
};

export default PlanOverview;
